<!--
  navItem „Provisionskonfiguration“ der ProvisionAgent-OBC — die Beratersicht.

  Aufbau in der Reihenfolge der Anforderung §2.2:
    Block 0  Steckbrief + Stichtagswahl + Gruppenvergleich-Schalter
    Block 1  je Provisionskomponente eine Tabelle, Zuordnungen in Entscheidungsreihenfolge
    Block 2  die Nullkombinationen zugeklappt
    Block 4  Vererbungspfad in der Zeilenaufklappung (FD-4 — kein Splitter, keine Workbench)

  Jede Tabelle beginnt mit zwei Sätzen Prosa (Vorbild: die Referenzseite
  report/provisionsmatrix-rathje.html) — keine Tabelle ohne Einleitung.
-->
<div class="cp-provision-agent-configuration flex flex-column gap-3">

    @if (!initialized) {

        <!--
          Ausdrücklicher Ladezustand, nicht nur ein Gerüst: eine halbe Sekunde ohne jede Rückmeldung
          liest sich genauso wie „leer", und leer ist auf dieser Seite eine gültige Aussage. Wer
          wartet, muss sehen, DASS er wartet, und worauf.
        -->
        <div class="flex align-items-center gap-2 text-color-secondary">
            <i class="fa-solid fa-spinner fa-spin"></i>
            <span>Provisionskonfiguration wird geladen …</span>
            @if (provisionAgentId) {
                <span class="text-sm">(Abrechnungskonto {{ provisionAgentId }})</span>
            }
        </div>
        <p-skeleton height="6rem"></p-skeleton>
        <p-skeleton height="18rem"></p-skeleton>

    } @else if (!provisionAgentId) {

        <p-message severity="info"
                   text="Kein Abrechnungskonto ausgewählt."
                   styleClass="w-full">
        </p-message>

    } @else if (loadError) {

        <!--
          Der Fehlschlag ERSETZT die Seite, er steht nicht darunter. Ein aus einer leeren Antwort
          gezeichneter Steckbrief („Abrechnungskonto · nicht bestimmbar · zum Stichtag keine") liest
          sich wie ein gültiges Ergebnis und war genau der stille Fehlschlag, gegen den dieser
          Zweig gebaut ist. Status, Bedeutung, angefragte Id, nächster Schritt — alles vier.
        -->
        <div class="surface-card border-round-lg p-5 flex flex-column align-items-center gap-3 text-center">

            <i class="fa-solid fa-plug-circle-exclamation text-4xl text-red-500"></i>

            <span class="text-lg font-semibold">{{ loadError }}</span>

            @if (loadErrorHint) {
                <span class="text-color-secondary" style="max-width: 44rem">{{ loadErrorHint }}</span>
            }

            <!-- Die angefragte Id und der Stichtag stehen sichtbar auf der Seite: ohne sie kann von
                 außen niemand feststellen, welches Objekt geöffnet war. -->
            <div class="flex flex-wrap justify-content-center gap-2">
                <p-tag [value]="'Angefragte Id ' + provisionAgentId"
                       icon="fa-regular fa-hashtag"
                       severity="secondary">
                </p-tag>
                <p-tag [value]="'Stichtag ' + germanDate(keyDateForDisplay())"
                       icon="fa-regular fa-calendar"
                       severity="secondary">
                </p-tag>
                @if (loadErrorStatus) {
                    <p-tag [value]="'HTTP ' + loadErrorStatus"
                           icon="fa-solid fa-triangle-exclamation"
                           severity="danger">
                    </p-tag>
                }
            </div>

            <span class="text-xs text-color-secondary">
                GET /mvsa/cp/provisionAgents/{{ provisionAgentId }}/provisionConfiguration
            </span>

            <p-button label="Erneut laden"
                      icon="fa-solid fa-rotate"
                      severity="primary"
                      (onClick)="refreshComponent()">
            </p-button>

        </div>

    } @else {

        <!--
          ══ The header — name, Provisionsart, Vermittlergruppe and key date, and it stays put
             while scrolling.

          The client's wish, verbatim: „Wenn man nach unten scrollt sollte der Name des beraters
          sowie die Vermittlergruppe sticky sein — dafür könnten diese Informationen einfach ganz
          oben angezeigt werden damit man nicht noch einen neuen overlay header bauen muss."
          That is exactly what stands here: the four facts move out of the profile block INTO this
          strip (moved, not duplicated) and the strip sticks to the view's scroll area via
          `position: sticky` — no second page header, no new interface to the full view.

          The key date is part of it because it is not decoration: the Vermittlergruppe depends on
          the key date, and „Gruppe X" without a date would be a false statement after a date
          change. It is still SET in the profile block below.
        -->
        <div class="cp-provision-agent-config-head surface-card border-round-lg shadow-1 px-3 py-2
                    flex flex-wrap align-items-center gap-3 text-sm">

            <span class="flex align-items-center gap-2">
                <i class="fa-regular fa-user text-color-secondary"></i>
                <span class="font-semibold">
                    {{ configuration?.provisionAgentName || 'Abrechnungskonto' }}
                </span>
            </span>

            <span class="flex align-items-center gap-2">
                <span class="text-color-secondary">Provisionsart</span>
                @if (configuration?.provisionTypeName) {
                    <span class="font-medium">{{ configuration.provisionTypeName }}</span>
                } @else {
                    <span class="text-color-secondary">nicht bestimmbar</span>
                }
            </span>

            @if (configuration?.stateInfo?.label) {
                <p-tag [value]="configuration.stateInfo.label"
                       [icon]="configuration.stateInfo.image"
                       [severity]="isResolved() ? 'success' : 'warn'"
                       [pTooltip]="configuration.stateInfo.description">
                </p-tag>
            }

            <span class="flex align-items-center gap-2">
                <span class="text-color-secondary">Vermittlergruppe</span>
                @if (configuration?.group?.provisionTypeGroupName) {
                    <span class="font-medium">{{ configuration.group.provisionTypeGroupName }}</span>
                    <span class="text-color-secondary">
                        ({{ periodLabel(configuration.group.startDate, configuration.group.endDate) }})
                    </span>
                } @else {
                    <span class="text-color-secondary">zum Stichtag keine</span>
                }
            </span>

            <span class="flex align-items-center gap-2 text-color-secondary">
                <i class="fa-regular fa-calendar"></i>
                <span>Stichtag {{ germanDate(configuration?.keyDate) || germanDate(keyDateForDisplay()) }}</span>
            </span>

        </div>

        <!-- ══ Block 0 — Steckbrief. Immer sichtbar, auch wenn es nichts aufzulösen gibt: er ist
             genau dann am wichtigsten, wenn die Antwort leer ausfällt. Name, Provisionsart,
             Zustand und Vermittlergruppe stehen jetzt im Streifen darüber und werden hier NICHT
             wiederholt; was hier bleibt, ist das Bedienbare und das Erklärende. -->
        <div class="surface-card border-round-lg p-3 flex flex-column gap-3">

            <div class="flex flex-wrap align-items-start justify-content-between gap-3">

                <div class="flex flex-column gap-2">

                    @if (configuration?.provisionTypeGoLiveDate) {
                        <span class="text-sm text-color-secondary">
                            Diese Provisionsart gilt ab {{ germanDate(configuration.provisionTypeGoLiveDate) }}.
                        </span>
                    }

                    <!-- Block 3: die vollständige Zuordnungshistorie steht bereits im
                         Navigationspunkt „Gruppen“ und wird hier nicht verdoppelt. -->
                    <span class="text-sm text-color-secondary">
                        Die vollständige Gruppenhistorie steht im Navigationspunkt „Gruppen“.
                    </span>

                </div>

                <!-- Stichtagswahl. Die Beschriftung sagt ausdrücklich, worauf sich das Datum
                     bezieht: die Konfiguration wird zum VERTRAGSBEGINN ausgewertet, nie zum
                     Laufdatum (Geschäftsregel 1). Ein nacktes „Stichtag“ wäre missverständlich. -->
                <div class="flex flex-column gap-2">

                    <label for="cpConfigKeyDate" class="text-sm font-medium">
                        Konfiguration für Verträge mit Beginn am
                    </label>

                    <p-datePicker inputId="cpConfigKeyDate"
                                  [(ngModel)]="keyDate"
                                  dateFormat="dd.mm.yy"
                                  placeholder="TT.MM.JJJJ"
                                  [showIcon]="true"
                                  appendTo="body"
                                  styleClass="w-14rem"
                                  (onSelect)="onKeyDateChange()"
                                  (onClear)="onKeyDateChange()">
                    </p-datePicker>

                    <div class="flex align-items-center gap-2">
                        <p-toggleSwitch inputId="cpConfigComparison"
                                        [(ngModel)]="includeGroupComparison"
                                        (onChange)="onComparisonChange()">
                        </p-toggleSwitch>
                        <label for="cpConfigComparison" class="text-sm">
                            Andere Vermittlergruppen vergleichen
                        </label>
                    </div>

                    <!--
                      Visibility switch. Deliberately placed next to the group comparison and not in
                      the tables: it applies to all components at once.

                      OFF by default, and that is not convenience: switched on, the view shows LESS
                      than the configuration holds, and a view that shrinks without being asked is
                      read as missing data, not as a filter. Unlike the group comparison it raises
                      NO server request — the rows are already there.
                    -->
                    <div class="flex align-items-center gap-2">
                        <p-toggleSwitch inputId="cpConfigHideWithoutOwnRow"
                                        [(ngModel)]="hideAssignmentsWithoutOwnRow"
                                        (onChange)="onHideWithoutOwnRowChange()">
                        </p-toggleSwitch>
                        <label for="cpConfigHideWithoutOwnRow"
                               class="text-sm"
                               pTooltip="Blendet die Zuordnungen aus, für die dieser Berater keinen eigenen Satz hat — die Zeilen „kein eigener Satz“. Reine Sichtbarkeit: nichts wird gelöscht, nichts umsortiert, und die Überschrift jedes Abschnitts sagt weiterhin, wie viele Zuordnungen konfiguriert sind. Bei eingeschaltetem Gruppenvergleich verschwinden mit der Zuordnung auch die Vergleichswerte der anderen Gruppen.">
                            Zuordnungen ohne eigenen Satz ausblenden
                        </label>
                    </div>

                </div>

            </div>

            <!-- Seitenweite Warnungen des Endpunkts. Werden gerendert, nie geschluckt. -->
            @for (warning of configuration?.warnings; track warning) {
                <p-message severity="warn" [text]="warning" styleClass="w-full"></p-message>
            }

        </div>

        <!--
          A sort that disappeared is NAMED. A key-date change and a group comparison reload and
          rebuild every table; the sort is lost on the way. Without this sentence the notice chip
          would vanish, the table would jump back into evaluation order and nobody would know why.
        -->
        @if (sortResetNotice) {
            <p-message severity="info" [text]="sortResetNotice" styleClass="w-full"></p-message>
        }

        @if (busy) {

            <!-- Nachladen (Stichtag gewechselt, Vergleich geschaltet): der Steckbrief bleibt stehen,
                 aber es muss sichtbar sein, dass gerade geladen wird. -->
            <div class="flex align-items-center gap-2 text-color-secondary">
                <i class="fa-solid fa-spinner fa-spin"></i>
                <span>Provisionskonfiguration wird geladen …</span>
            </div>
            <p-skeleton height="18rem"></p-skeleton>

        } @else if (isNoProvisionType()) {

            <!-- Zustand: gar keine Provisionsart bestimmbar. Klartext statt leerer Seite. -->
            <div class="surface-card border-round-lg p-5 flex flex-column align-items-center gap-3 text-center">
                <i class="fa-solid fa-ban text-4xl text-color-secondary"></i>
                <span class="text-lg font-semibold">Keine Provisionsart hinterlegt</span>
                <span class="text-color-secondary">
                    Für dieses Abrechnungskonto ist zum {{ germanDate(configuration?.keyDate) }}
                    keine Provisionsart ermittelbar — der Berater ist an diesem Tag keiner
                    Vermittlergruppe zugeordnet, auch nicht in einer anderen Provisionsart.
                </span>
                <span class="text-color-secondary text-sm">
                    Ein früherer Stichtag zeigt die damals gültige Zuordnung.
                </span>
            </div>

        } @else if (isNoGroupAtKeyDate()) {

            <!--
              Der HÄUFIGE Fall (320 von 355 Abrechnungskonten im Referenzbestand): die Provisionsart
              ist bekannt, der Berater hat an diesem Tag darin keine Gruppe. Ausdrückliche
              Auftraggeberentscheidung „Erklärung statt Leere“ — es wird gesagt, WORAN es liegt,
              samt der Gruppen, die er anderswo hat.
            -->
            <div class="surface-card border-round-lg p-4 flex flex-column gap-3">

                <div class="flex align-items-center gap-2">
                    <i class="fa-solid fa-user-slash text-2xl text-orange-500"></i>
                    <span class="text-lg font-semibold">
                        Zum {{ germanDate(configuration?.keyDate) }} keine Vermittlergruppe in dieser Provisionsart
                    </span>
                </div>

                <span class="text-color-secondary">
                    Die Provisionskonfiguration wird über die Vermittlergruppe aufgelöst. Ohne eine
                    Gruppe in
                    <span class="font-medium">{{ configuration?.provisionTypeName || 'dieser Provisionsart' }}</span>
                    gibt es an diesem Stichtag nichts aufzulösen — das ist eine gültige Auskunft,
                    kein Fehler.
                </span>

                @if (configuration?.otherProvisionTypeGroups?.length) {

                    <span class="font-medium">
                        In diesen Provisionsarten ist der Berater zugeordnet:
                    </span>

                    <div class="flex flex-column gap-2">
                        @for (other of configuration.otherProvisionTypeGroups; track other.provisionTypeGroupId) {
                            <div class="flex flex-wrap align-items-center gap-2 surface-50 border-round-sm p-2">
                                <span class="font-medium">{{ other.provisionTypeName }}</span>
                                <span class="text-color-secondary">·</span>
                                <span>{{ other.provisionTypeGroupName }}</span>
                                <span class="text-color-secondary text-sm">
                                    ({{ periodLabel(other.startDate, other.endDate) }})
                                </span>
                                @if (other.validAtKeyDate) {
                                    <p-tag value="zum Stichtag gültig" severity="success"></p-tag>
                                }
                            </div>
                        }
                    </div>

                    <span class="text-color-secondary text-sm">
                        Um deren Konfiguration zu sehen, muss die betreffende Provisionsart gewählt
                        werden; die vollständige Zuordnungshistorie steht im Navigationspunkt
                        „Gruppen“.
                    </span>

                } @else {

                    <span class="text-color-secondary text-sm">
                        Der Berater ist an diesem Stichtag auch in keiner anderen Provisionsart
                        einer Gruppe zugeordnet. Ein früherer Stichtag zeigt die damals gültige
                        Zuordnung.
                    </span>

                }

            </div>

        } @else if (!blocks.length) {

            <p-message severity="info"
                       text="Für diesen Stichtag sind keine Provisionskomponenten konfiguriert."
                       styleClass="w-full">
            </p-message>

        } @else {

            <!--
              Sections hidden entirely are NAMED. A section that disappears without its name
              standing anywhere cannot be told from a Provisionskomponente that does not exist at
              all — exactly the small untruth this page is built against.
            -->
            @if (hiddenBlockNames.length) {
                <div class="surface-50 border-round-sm p-2 flex flex-wrap align-items-center gap-2 text-sm">
                    <i class="fa-solid fa-eye-slash text-color-secondary"></i>
                    <span class="text-color-secondary">
                        Ohne eigenen Satz und deshalb ausgeblendet:
                    </span>
                    @for (name of hiddenBlockNames; track name) {
                        <span class="font-medium">{{ name }}</span>
                    }
                    <span class="text-color-secondary">
                        — für diese Komponenten ist für den Berater kein vergütender Satz hinterlegt. Schalter
                        „Zuordnungen ohne eigenen Satz ausblenden“ aus, um sie zu sehen.
                    </span>
                </div>
            }

            <!-- ══ Block 1 — je Provisionskomponente ein Abschnitt ══ -->
            @for (block of blocks; track block.component.componentId) {

                @if (!block.hiddenBlock) {

                <div class="surface-card border-round-lg p-3 flex flex-column gap-3">

                    <div class="flex flex-wrap align-items-center gap-2">
                        <i class="fa-regular fa-layer-group text-color-secondary"></i>
                        <span class="text-lg font-semibold">{{ block.component.componentName }}</span>
                        <!-- Wie viel in diesem Block steht, ohne bis ans Ende zu scrollen. Zwei
                             Zahlen, weil es zwei sind: eine Zuordnung mit Bändern belegt mehrere
                             Zeilen. Was NICHT mitgezählt ist, sagt der Tooltip. -->
                        <p-tag [value]="block.countLabel"
                               severity="secondary"
                               [pTooltip]="block.countTooltip">
                        </p-tag>
                    </div>

                    <!--
                      Introduction: what is about to be seen and what it means.

                      The sentence CHANGES while a sort is active. In its normal state the order of
                      this table is a statement („die erste passende gewinnt"); a sort takes exactly
                      that statement away. Leaving it standing while the table is sorted by factor
                      would be the one untruth this page cannot afford.
                    -->
                    @if (block.payingSort) {
                        <span class="text-sm text-color-secondary">
                            Die Zuordnungen sind nach „{{ block.payingSort.columnLabel }}“ sortiert —
                            maßgeblich ist je Zuordnung ihr
                            {{ block.payingSort.order > 0 ? 'kleinster' : 'größter' }} Wert, damit
                            Bänder und Vergleichszeilen bei ihrer Zuordnung bleiben. Die Reihenfolge,
                            in der die Berechnung sie prüft, ist so nicht mehr sichtbar —
                            „Prüfreihenfolge“ stellt sie wieder her. Jeder Wert trägt rechts die
                            Angabe, aus welcher Matrix er stammt.
                        </span>
                    } @else {
                        <span class="text-sm text-color-secondary">
                            Die Zuordnungen stehen in der Reihenfolge, in der die Berechnung sie prüft —
                            von oben nach unten von der speziellsten zur allgemeinsten. Die erste
                            passende gewinnt. Eine Zuordnung „Alle Verträge (unbedingt)“ hat keinen
                            Bedingungssatz und greift für jeden Vertrag, den keine speziellere trifft;
                            die Zeile „alles Übrige“ unter der Tabelle greift, wenn keine Zuordnung
                            passt. Jeder Wert trägt rechts die Angabe, aus welcher Matrix er stammt.
                        </span>
                    }

                    <!--
                      KEIN eigener Scrollbereich in der Tabelle (Auftraggeberentscheidung): die
                      Tabelle steht in voller Höhe im Seitenfluss, gescrollt wird die Seite
                      (`.cp-provision-agent-full` weiter oben). Ein Kasten in einem Kasten, jeder
                      mit eigenem Rollbalken, war beim Lesen langer Blöcke im Weg.
                    -->
                    <!--
                      `[customSort]` instead of the built-in sort: PrimeNG sorts ROWS by a field
                      path, but here several rows belong together (bands of the same assignment,
                      comparison rows of other groups — measured up to 47 rows on one assignment).
                      `sortRows` therefore re-orders PER ASSIGNMENT, in place, because PrimeNG calls
                      its own `_filter()` right afterwards.
                    -->
                    <p-table #payingTable
                             [value]="block.payingRows"
                             dataKey="rowKey"
                             styleClass="p-datatable-sm p-datatable-striped"
                             [customSort]="true"
                             (sortFunction)="sortRows(block, 'paying', $event)"
                             [globalFilterFields]="rowFilterFields">

                        <!--
                          Filter und Trefferzahl stehen im #caption. Das ist der
                          `p-datatable-header` — ein Geschwisterelement VOR dem scrollenden
                          Container — und bleibt deshalb stehen, genau wie der Spaltenkopf.
                        -->
                        <ng-template #caption>
                            <div class="flex flex-wrap align-items-center justify-content-between gap-2">

                                <div class="flex align-items-center gap-2 flex-grow-1">

                                    <i class="fa-regular fa-magnifying-glass text-color-secondary"></i>

                                    <input pInputText
                                           type="text"
                                           class="flex-grow-1 w-6rem"
                                           aria-label="Zeilen filtern"
                                           placeholder="Zeilen filtern — z. B. Gesellschaft, Vertragsart, Matrix"
                                           [value]="block.filterTerm"
                                           (input)="applyRowFilter(block, payingTable, $any($event.target).value)">

                                    @if (block.filterTerm) {
                                        <p-button type="button"
                                                  icon="fa-regular fa-xmark"
                                                  [text]="true"
                                                  severity="secondary"
                                                  size="small"
                                                  pTooltip="Filter leeren"
                                                  (onClick)="clearRowFilter(block, payingTable)">
                                        </p-button>
                                    }

                                </div>

                                <div class="flex flex-wrap align-items-center gap-2">

                                    <!-- The visibility switch sits far up in the profile block and
                                         scrolls away; down here at the table it must still say that
                                         a part is missing. Bound to the NUMBER, not to the switch: a
                                         section with nothing to hide carries no notice either. -->
                                    @if (block.hiddenPayingAssignmentCount) {
                                        <p-tag [value]="block.hiddenPayingAssignmentCount + ' Zuordnungen ohne eigenen Satz ausgeblendet'"
                                               icon="fa-solid fa-eye-slash"
                                               styleClass="cp-config-tag-warn"
                                               pTooltip="Für diese Zuordnungen ist für diesen Berater kein eigener Satz hinterlegt. Sie stehen weiterhin in der Konfiguration — der Schalter „Zuordnungen ohne eigenen Satz ausblenden“ oben holt sie zurück.">
                                        </p-tag>
                                    }

                                    <!-- While a sort is active it is visible at a glance that the
                                         table NO LONGER shows the evaluation order — and beside it
                                         the one button that brings it back. -->
                                    @if (block.payingSort) {
                                        <p-tag [value]="block.payingSort.chipLabel"
                                               icon="fa-solid fa-arrow-down-wide-short"
                                               styleClass="cp-config-tag-warn"
                                               [pTooltip]="block.payingSort.chipTooltip">
                                        </p-tag>
                                        <p-button type="button"
                                                  label="Prüfreihenfolge"
                                                  icon="fa-regular fa-rotate-left"
                                                  [text]="true"
                                                  severity="secondary"
                                                  size="small"
                                                  pTooltip="Zurück in die Reihenfolge, in der die Berechnung die Zuordnungen prüft"
                                                  (onClick)="resetSort(block, 'paying', payingTable)">
                                        </p-button>
                                    }

                                    @if (block.filterTerm || block.hiddenPayingRowCount) {
                                        <!-- Ein gefilterter Ausschnitt darf nie wie die ganze Wahrheit
                                             aussehen. Solange etwas verborgen ist — durch den Textfilter
                                             ODER durch den Sichtbarkeitsschalter — steht hier, wie viele
                                             von wie vielen Zeilen zu sehen sind. Der Nenner bleibt die
                                             volle Zeilenzahl des Abschnitts; der Zähler zählt, was
                                             tatsächlich gezeichnet wird. -->
                                        <span class="text-sm font-medium white-space-nowrap">
                                            {{ payingTable.filteredValue ? payingTable.filteredValue.length : block.payingRows.length }}
                                            von {{ block.payingRowCount }} Zeilen
                                        </span>
                                    }

                                </div>

                            </div>
                        </ng-template>

                        <!--
                          The column header stands INLINE in this p-table and not in a shared
                          template: `pSortableColumn` and `p-sortIcon` obtain the Table instance via
                          DI, and a template rendered through `*ngTemplateOutlet` resolves DI at its
                          DECLARATION site — outside every table, with NG0201 as the result. The row
                          template already failed on exactly that once (see #rowTemplate).

                          „Priorität" is deliberately NOT sortable: it only appears in the expansion
                          and is the configured evaluation order itself.
                        -->
                        <ng-template #header>
                            <tr>
                                <th style="width: 3rem"></th>
                                @for (column of block.dimensionColumns; track column.key) {
                                    <th [pSortableColumn]="block.dimensionSortFields[$index]">
                                        {{ column.label }}
                                        <p-sortIcon [field]="block.dimensionSortFields[$index]"></p-sortIcon>
                                    </th>
                                }
                                <th pSortableColumn="factor">
                                    Faktor
                                    <p-sortIcon field="factor"></p-sortIcon>
                                </th>
                                @if (block.hasTermBand) {
                                    <th pSortableColumn="term">
                                        Laufzeit
                                        <p-sortIcon field="term"></p-sortIcon>
                                    </th>
                                }
                                @if (block.hasEntryAgeBand) {
                                    <th pSortableColumn="entryAge">
                                        Eintrittsalter
                                        <p-sortIcon field="entryAge"></p-sortIcon>
                                    </th>
                                }
                                @if (block.hasLiability) {
                                    <th pSortableColumn="liability">
                                        Haftung
                                        <p-sortIcon field="liability"></p-sortIcon>
                                    </th>
                                }
                                <th pSortableColumn="axis">
                                    Achse
                                    <p-sortIcon field="axis"></p-sortIcon>
                                </th>
                                <th pSortableColumn="origin">
                                    Herkunft des Werts
                                    <p-sortIcon field="origin"></p-sortIcon>
                                </th>
                            </tr>
                        </ng-template>

                        <ng-template #body let-displayRow let-expanded="expanded">
                            <ng-container
                                    *ngTemplateOutlet="rowTemplate; context: {$implicit: displayRow, expanded: expanded, block: block, table: payingTable}">
                            </ng-container>
                        </ng-template>

                        <ng-template #expandedrow let-displayRow>
                            <ng-container
                                    *ngTemplateOutlet="detailTemplate; context: {$implicit: displayRow, block: block}">
                            </ng-container>
                        </ng-template>

                        <ng-template #emptymessage>
                            <tr>
                                <td [attr.colspan]="block.columnCount" class="text-center text-color-secondary p-3">
                                    @if (block.filterTerm) {
                                        <!-- Ohne diesen Zweig behauptet ein leerer Filtertreffer
                                             „diese Komponente vergütet nichts" — eine falsche
                                             Aussage über den Bestand statt über den Ausschnitt. -->
                                        Keine Zeile passt zum Filter „{{ block.filterTerm }}“.
                                        Die Zeilen sind vorhanden, nur ausgeblendet — Filter leeren,
                                        um wieder alle zu sehen.
                                    } @else if (block.hiddenPayingAssignmentCount) {
                                        <!-- Exactly the case from the stock: three of four components of
                                             this account consist ONLY of „kein eigener Satz". Without
                                             this branch it would say „ohne Vergütung" here, which would
                                             be a statement about the stock instead of the excerpt. -->
                                        Für diese Komponente hat der Berater keinen einzigen eigenen
                                        Satz — alle {{ block.hiddenPayingAssignmentCount }} Zuordnungen sind
                                        durch den Schalter „Zuordnungen ohne eigenen Satz ausblenden“
                                        verborgen. Schalter aus, um sie zu sehen.
                                    } @else {
                                        Alle Kombinationen dieser Komponente sind ohne Vergütung.
                                    }
                                </td>
                            </tr>
                        </ng-template>

                    </p-table>

                    <!--
                      ══ Die Zeile „alles Übrige" ══
                      EIN Konfigurationssatz, EINE Zeile. Er stammt aus der Wurzelmatrix der
                      Vererbung und wird von jeder Zuordnung geerbt, weshalb er in der Antwort
                      unter jeder einzelnen steht — gemessen 190-mal derselbe Satz. Als Tabellen-
                      zeile gerendert las er sich 190-mal als eigene Aussage „Faktor 0", also als
                      „dafür gibt es nichts"; tatsächlich ist er die Auffangregel mit der
                      niedrigsten Priorität und gehört genau einmal ans Ende.

                      Bewusst AUSSERHALB der p-table: er ist keine Zuordnungszeile, darf nicht
                      mitgezählt werden und darf vor allem nicht wegfiltern — er gilt immer, auch
                      wenn der Filter gerade nur drei Zeilen zeigt.
                    -->
                    @if (block.universalFallbackRows.length) {

                        <div class="surface-50 border-round-sm p-3 flex flex-column gap-2">

                            <div class="flex flex-wrap align-items-center gap-2">
                                <i class="fa-regular fa-arrow-turn-down-left text-color-secondary"></i>
                                <span class="font-medium font-italic">alles Übrige</span>
                                <p-tag value="niedrigste Priorität"
                                       severity="secondary"
                                       pTooltip="Diese Zeile wird erst geprüft, wenn keine Zuordnung darüber passt.">
                                </p-tag>
                            </div>

                            <span class="text-xs text-color-secondary">
                                Gilt für alles, was keine Regel in der Tabelle darüber trifft. Der
                                Satz stammt aus der Wurzelmatrix der Vererbung und gilt ohne
                                Einschränkung — er ist ein einziger Konfigurationssatz, den jede
                                Zuordnung erbt, und steht deshalb hier einmal statt in jeder Zeile.
                            </span>

                            @for (fallback of block.universalFallbackRows; track $index) {
                                <div class="flex flex-wrap align-items-center gap-2">

                                    @if (fallback.comparisonGroupName) {
                                        <p-tag [value]="fallback.comparisonGroupName"
                                               icon="fa-solid fa-users"
                                               severity="secondary"
                                               pTooltip="Wert einer anderen Vermittlergruppe — Vergleich, nicht der eigene Satz">
                                        </p-tag>
                                    }

                                    <cp-form-field-output-factor
                                            [value]="fallback.row?.factor"
                                            [calculationMode]="fallback.row?.calculationMode"
                                            [calculationModeInfo]="fallback.row?.calculationModeInfo">
                                    </cp-form-field-output-factor>

                                    <!--
                                      Liability type WITH its origin — the same four-way split as in
                                      the table and in the expansion (matrix / assignment / general /
                                      no liability).

                                      Up to here this spot showed the liability type without its
                                      source, as the only place in the view. Of all places it matters
                                      here: measured, the catch-all rows are where the „generell"
                                      cases sit — on account 9000001068 ALL 108 rows with a default-row
                                      hit carry `universalFallback`. The word this package is meant to
                                      make visible was missing exactly where it always applies.
                                    -->
                                    <div class="flex flex-column">
                                        @if (fallback.liabilityTypeMissing) {
                                            <span class="text-xs text-color-secondary font-italic">
                                                Haftungstyp nicht aktiv
                                            </span>
                                        } @else if (fallback.row?.liabilityTypeName) {
                                            <span class="text-xs text-color-secondary">
                                                {{ fallback.row.liabilityTypeName }}
                                            </span>
                                        }
                                        <span class="text-xs text-color-secondary font-medium"
                                              [pTooltip]="fallback.liabilityOriginDetail">
                                            {{ fallback.liabilityOriginTitle }}
                                        </span>
                                    </div>

                                    <cp-form-field-output-origin
                                            [value]="fallback.row?.origin"
                                            [enumInfo]="fallback.row?.originInfo"
                                            [matrixName]="fallback.row?.originMatrixName">
                                    </cp-form-field-output-origin>

                                </div>
                            }

                        </div>

                    }

                    <!-- ══ Block 2 — die Nullkombinationen, standardmäßig zugeklappt ══
                         Faktor 0 ist eine Aussage („dafür gibt es nichts“), aber sie ersäuft die
                         echten Zeilen. Verborgen wird nur die Sicht, nie der Inhalt. -->
                    @if (block.zeroAssignmentCount) {

                        <p-button
                                [label]="block.zeroBlockExpanded
                                    ? block.zeroToggleLabelHide
                                    : block.zeroToggleLabelShow"
                                [icon]="block.zeroBlockExpanded
                                    ? 'fa-regular fa-chevron-up' : 'fa-regular fa-chevron-down'"
                                severity="secondary"
                                [text]="true"
                                size="small"
                                styleClass="align-self-start"
                                (onClick)="toggleZeroBlock(block)">
                        </p-button>

                        @if (block.zeroBlockExpanded) {

                            <span class="text-sm text-color-secondary">
                                Für diese Kombinationen ist ausdrücklich ein Faktor 0 hinterlegt —
                                es gibt dafür keine Vergütung. Das ist eine bewusste Angabe der
                                Konfiguration, kein fehlender Wert.
                            </span>

                            <!-- Sort state of this table. Stands ABOVE the table and not in its
                                 `#caption`: a conditionally declared caption template is a
                                 content-query gamble, and the strip would otherwise be there
                                 permanently, mostly empty. The reference to `#zeroTable` reaches
                                 forward within the same view — allowed, and the simplest here. -->
                            @if (block.zeroSort) {
                                <div class="flex flex-wrap align-items-center gap-2">
                                    <p-tag [value]="block.zeroSort.chipLabel"
                                           icon="fa-solid fa-arrow-down-wide-short"
                                           styleClass="cp-config-tag-warn"
                                           [pTooltip]="block.zeroSort.chipTooltip">
                                    </p-tag>
                                    <p-button type="button"
                                              label="Prüfreihenfolge"
                                              icon="fa-regular fa-rotate-left"
                                              [text]="true"
                                              severity="secondary"
                                              size="small"
                                              pTooltip="Zurück in die Reihenfolge, in der die Berechnung die Zuordnungen prüft"
                                              (onClick)="resetSort(block, 'zero', zeroTable)">
                                    </p-button>
                                </div>
                            }

                            <!-- Ebenfalls ohne eigenen Scrollbereich, gleicher Grund. Sortierbar wie
                                 die Haupttabelle, mit eigenem Zustand: die beiden Tabellen sind zwei
                                 Aussagen und werden getrennt sortiert und getrennt zurückgesetzt. -->
                            <p-table #zeroTable
                                     [value]="block.zeroRows"
                                     dataKey="rowKey"
                                     [customSort]="true"
                                     (sortFunction)="sortRows(block, 'zero', $event)"
                                     styleClass="p-datatable-sm">

                                <ng-template #header>
                                    <tr>
                                        <th style="width: 3rem"></th>
                                        @for (column of block.dimensionColumns; track column.key) {
                                            <th [pSortableColumn]="block.dimensionSortFields[$index]">
                                                {{ column.label }}
                                                <p-sortIcon [field]="block.dimensionSortFields[$index]"></p-sortIcon>
                                            </th>
                                        }
                                        <th pSortableColumn="factor">
                                            Faktor
                                            <p-sortIcon field="factor"></p-sortIcon>
                                        </th>
                                        @if (block.hasTermBand) {
                                            <th pSortableColumn="term">
                                                Laufzeit
                                                <p-sortIcon field="term"></p-sortIcon>
                                            </th>
                                        }
                                        @if (block.hasEntryAgeBand) {
                                            <th pSortableColumn="entryAge">
                                                Eintrittsalter
                                                <p-sortIcon field="entryAge"></p-sortIcon>
                                            </th>
                                        }
                                        @if (block.hasLiability) {
                                            <th pSortableColumn="liability">
                                                Haftung
                                                <p-sortIcon field="liability"></p-sortIcon>
                                            </th>
                                        }
                                        <th pSortableColumn="axis">
                                            Achse
                                            <p-sortIcon field="axis"></p-sortIcon>
                                        </th>
                                        <th pSortableColumn="origin">
                                            Herkunft des Werts
                                            <p-sortIcon field="origin"></p-sortIcon>
                                        </th>
                                    </tr>
                                </ng-template>

                                <ng-template #body let-displayRow let-expanded="expanded">
                                    <ng-container
                                            *ngTemplateOutlet="rowTemplate; context: {$implicit: displayRow, expanded: expanded, block: block, table: zeroTable}">
                                    </ng-container>
                                </ng-template>

                                <ng-template #expandedrow let-displayRow>
                                    <ng-container
                                            *ngTemplateOutlet="detailTemplate; context: {$implicit: displayRow, block: block}">
                                    </ng-container>
                                </ng-template>

                            </p-table>

                        }

                    }

                </div>

                }

            }

        }

    }

</div>


<!--
  ══ Die Zeile ══
  Als wiederverwendbares Template, weil die Nullkombinationen dieselbe Zeile in einer zweiten
  Tabelle rendern. Zwei Kopien wären zwei Wahrheiten.
-->
<ng-template #rowTemplate let-displayRow let-expanded="expanded" let-block="block" let-table="table">

    <tr [class.cp-comparison-row]="displayRow.comparisonGroupName"
        [class.cp-agent-row]="displayRow.agentAxis">

        <!-- Aufklapp-Schalter: FD-4, Zeilenaufklappung statt Splitter.

             The toggle is driven through the Table INSTANCE handed in via the template context,
             NOT through the [pRowToggler] directive. This template is shared by all tables and is
             instantiated with *ngTemplateOutlet, so an embedded view created from it resolves
             directive DI against this DECLARATION site — which lies outside every <p-table>.
             pRowToggler injects Table in its constructor, found no provider there, escaped to the
             module injector and threw NG0201, which aborted the rendering of every body row.
             Table.toggleRow(rowData, event) is the very method the directive calls and needs no DI. -->
        <td>
            <p-button type="button"
                      (onClick)="table.toggleRow(displayRow, $event)"
                      [text]="true"
                      severity="secondary"
                      size="small"
                      [icon]="expanded ? 'fa-regular fa-chevron-down' : 'fa-regular fa-chevron-right'"
                      pTooltip="Vererbungspfad und Details ein-/ausklappen">
            </p-button>
        </td>

        <!-- Dimensionszellen — nur die belegten Spalten, und nur auf der ersten Zeile einer
             Zuordnung; die Folgezeilen sind Bänder derselben Zuordnung.

             Die Folgezeile darf NICHT einfach leer bleiben. In derselben Spalte heißt ein
             ausgeschriebenes „alle“, dass die Zuordnung dieses Merkmal nicht einschränkt — eine
             leere Zelle daneben liest sich damit als fehlender Wert oder als Datenfehler, nicht
             als „gehört zur Zeile darüber“. Genau so wurde sie gemeldet. Die Folgezeile sagt
             deshalb ausdrücklich, wozu sie gehört, in derselben Klartext-Manier wie jeder andere
             Zustand dieser Tabelle. -->
        @for (column of block.dimensionColumns; track column.key) {
            <td>
                @if (displayRow.leadRow) {
                    @if (displayRow.assignment.fallback && $first) {
                        <!-- `fallback` = the assignment has NO condition set: unconditional, it
                             applies to every contract. Not "a row without dimension values" — an
                             all-wildcard condition set is no fallback. -->
                        <div class="flex flex-column gap-1"
                             pTooltip="Diese Zuordnung hat keinen Bedingungssatz — sie gilt für jeden Vertrag, den keine speziellere Zuordnung darüber trifft.">
                            <span class="font-medium font-italic white-space-nowrap">Alle Verträge (unbedingt)</span>
                            <span class="text-xs text-color-secondary">ohne Bedingungssatz</span>
                        </div>
                    } @else if (displayRow.dimensionCells[$index]) {
                        <!-- Values come from the INCLUDE conditions of the set; several
                             conditions arrive as "A oder B" — the cell wraps instead of
                             stretching the column. -->
                        <span class="inline-block max-w-20rem white-space-normal line-height-3">{{ displayRow.dimensionCells[$index] }}</span>
                    } @else {
                        <span class="text-color-secondary">alle</span>
                    }
                } @else if (displayRow.comparisonGroupName && $first) {
                    <span class="text-sm text-color-secondary">
                        <i class="fa-solid fa-users mr-1"></i>{{ displayRow.comparisonGroupName }}
                    </span>
                } @else if ($first) {
                    <span class="text-sm text-color-secondary font-italic white-space-nowrap"
                          [pTooltip]="displayRow.continuationHint">
                        <i class="fa-solid fa-turn-up fa-rotate-90 mr-1"></i>weiteres Band
                    </span>
                }
            </td>
        }

        <!-- Faktor + Berechnungsart als registrierte Ausgabe-Feldkomponente (H5).

             Ohne Zeile gibt es keinen Faktor, und ein leeres Feld wäre hier die schlechteste
             Auskunft: die Zeile steht ja, weil die Kombination existiert. Statt der Zahl steht
             deshalb im Klartext, dass es für sie keinen eigenen Satz gibt — der volle Satz samt
             Verweis auf „alles Übrige“ im Tooltip. -->
        <td>
            @if (displayRow.row) {
                <cp-form-field-output-factor
                        [value]="displayRow.row.factor"
                        [calculationMode]="displayRow.row.calculationMode"
                        [calculationModeInfo]="displayRow.row.calculationModeInfo">
                </cp-form-field-output-factor>
            } @else {
                <span class="text-xs text-color-secondary font-italic white-space-nowrap"
                      [pTooltip]="displayRow.noRateHint">
                    kein eigener Satz
                </span>
            }
        </td>

        @if (block.hasTermBand) {
            <td class="white-space-nowrap">
                @if (displayRow.termBandText) {
                    {{ displayRow.termBandText }}
                } @else {
                    <span class="text-color-secondary">alle</span>
                }
            </td>
        }

        @if (block.hasEntryAgeBand) {
            <td class="white-space-nowrap">
                @if (displayRow.entryAgeBandText) {
                    {{ displayRow.entryAgeBandText }}
                } @else {
                    <span class="text-color-secondary">alle</span>
                }
            </td>
        }

        @if (block.hasLiability) {
            <td>
                @if (displayRow.liabilityTypeMissing) {
                    <!-- Source known, type not nameable (inactive liability type). The cell
                         does NOT fall back to „—": „—" reads as „keine Haftung", which is a
                         different statement. The page warning names the case in plain words. -->
                    <span class="text-color-secondary font-italic">Haftungstyp nicht aktiv</span>
                    <div class="text-xs text-color-secondary">
                        {{ displayRow.liabilityOriginTitle }}
                    </div>
                } @else if (displayRow.row?.liabilityTypeName) {
                    <span>{{ displayRow.row.liabilityTypeName }}</span>
                    <!-- Business rule 5: the display names the SOURCE of the liability type, not only
                         the value — in the same four-way split as the expansion
                         (matrix / assignment / general / no liability). -->
                    <div class="text-xs text-color-secondary">
                        {{ displayRow.liabilityOriginTitle }}
                    </div>
                    @if (displayRow.row.liabilityAmbiguous) {
                        <!-- Ein Zusatz, keine Warnung: der Wert IST die beste Antwort, sie steht
                             nur unter Vorbehalt. Deshalb dieselbe stille Zeile wie die Quelle
                             darüber — gedämpft, klein, untergeordnet. -->
                        <div class="text-xs text-color-secondary font-italic"
                             pTooltip="Die Haftungszuordnung schränkt ein Merkmal ein, das diese Zuordnung offen lässt. Welche Haftungsart am Ende gilt, entscheidet sich erst am konkreten Vertrag.">
                            abhängig vom Vertrag
                        </div>
                    }
                } @else if (displayRow.assignment.liabilityTypeOverwriteName) {
                    <span>{{ displayRow.assignment.liabilityTypeOverwriteName }}</span>
                    <div class="text-xs text-color-secondary">Überschreibung an der Zuordnung</div>
                } @else if (displayRow.row) {
                    <!-- There is a row but no liability type: no liability period, no
                         charge-back. That is a statement and is written as one. -->
                    <span class="text-color-secondary">keine Haftung</span>
                } @else {
                    <span class="text-color-secondary">—</span>
                }
            </td>
        }

        <!-- Achse sichtbar machen (Gate-Auflage FD-1). Heute liefert die Beraterachse null
             Zeilen — die Darstellung steht trotzdem, sobald eine existiert. -->
        <td>
            @if (displayRow.comparisonGroupName) {
                <p-tag [value]="displayRow.comparisonGroupName"
                       icon="fa-solid fa-users"
                       severity="secondary"
                       pTooltip="Wert einer anderen Vermittlergruppe — Vergleich, nicht der eigene Satz">
                </p-tag>
            } @else if (displayRow.row?.axisInfo?.label) {
                <div class="flex flex-column gap-1">
                    <p-tag [value]="displayRow.row.axisInfo.label"
                           [icon]="displayRow.row.axisInfo.image"
                           [severity]="displayRow.agentAxis ? 'info' : 'secondary'"
                           [pTooltip]="displayRow.row.axisInfo.description">
                    </p-tag>
                    @if (displayRow.row.beatsGroupRow) {
                        <span class="text-xs text-primary font-medium">
                            schlägt die Gruppenzeile
                        </span>
                    }
                    @if (displayRow.row.shadowedByAgentRow) {
                        <span class="text-xs text-color-secondary">
                            von einer persönlichen Zeile verdeckt
                        </span>
                    }
                </div>
            } @else {
                <span class="text-color-secondary">—</span>
            }
        </td>

        <!-- Herkunfts-Chip als registrierte Ausgabe-Feldkomponente (H5) + Warn-Chip für die
             lautlos endende Vererbungskette (FD-2). -->
        <td>
            <div class="flex flex-column gap-1">

                <cp-form-field-output-origin
                        [value]="displayRow.row?.origin"
                        [enumInfo]="displayRow.row?.originInfo"
                        [matrixName]="displayRow.row?.originMatrixName">
                </cp-form-field-output-origin>

                @if (displayRow.leadRow && displayRow.assignment.narrowedByExclude) {
                    <!-- EXCLUDE conditions are not subtracted by the static view: the dimension
                         values shown are an over-approximation. `severity` alone is repainted by
                         the global .p-tag override, hence the local colour class as well. -->
                    <p-tag value="eingeschränkt durch Ausschluss"
                           icon="fa-solid fa-filter-circle-xmark"
                           severity="warn"
                           styleClass="cp-config-tag-warn"
                           pTooltip="Diese Zuordnung hat Ausschluss-Bedingungen – die angezeigten Dimensionen können eingeschränkt sein.">
                    </p-tag>
                }

                @if (displayRow.leadRow && displayRow.assignment.inheritanceChainTruncated) {
                    <p-tag [value]="'Vererbungskette abgeschnitten'"
                           icon="fa-solid fa-triangle-exclamation"
                           styleClass="cp-config-tag-danger"
                           [pTooltip]="displayRow.assignment.warning
                               || 'Die Vererbungskette endet an einer inaktiven oder fremden Elternmatrix.'">
                    </p-tag>
                }

                <!-- Row outside its own validity. Measured, this does not occur in the stock
                     (0 of 10.443 rows), in code it can: the runtime index filters on the status,
                     not on the period. Without this chip such a row could not be told from a valid
                     one — yet it can win for no contract with this start. The former green counter
                     chip was dropped without replacement: it stood on all 10.443 rows and therefore
                     said nothing. -->
                @if (displayRow.invalidAtKeyDate) {
                    <p-tag value="zum Stichtag nicht gültig"
                           icon="fa-solid fa-calendar-xmark"
                           styleClass="cp-config-tag-warn"
                           pTooltip="Diese Zeile gilt für Verträge mit dem gewählten Beginn nicht — die Berechnung überspringt sie.">
                    </p-tag>
                }

                @if (displayRow.leadRow && displayRow.assignmentRowCount > 1) {
                    <span class="text-xs text-color-secondary">
                        {{ displayRow.assignmentRowCount }} Bänder
                    </span>
                }

            </div>
        </td>

    </tr>

</ng-template>


<!--
  ══ Das Detail zur Zeile (Block 4) ══
  Der Vererbungspfad kommt als fertig zerlegte Schrittliste vom Endpunkt — hier wird nichts
  zerteilt und nichts zusammengesetzt.
-->
<ng-template #detailTemplate let-displayRow let-block="block">

    <tr>
        <td [attr.colspan]="block.columnCount" class="surface-50">

            <div class="p-3 flex flex-column gap-3">

                <div class="flex flex-wrap gap-5">

                    <!--
                      Die beiden Ids, mit denen sich die angezeigte Zeile im Bestand wiederfinden
                      lässt. Sie stehen ZUERST, weil genau danach greift, wer den Datensatz
                      nachschlagen will — und sie sind ausdrücklich BENANNT: „Zuordnung" und
                      „Matrixzeile" sind zwei verschiedene Tabellen, und sie zu verwechseln ist
                      der Fehler, den diese Anzeige verhindern soll. Die Zahl selbst trägt
                      `select-all`, ein Klick markiert sie also vollständig zum Kopieren; das
                      ÖFFNEN hängt am Symbol daneben, damit Kopieren und Navigieren nicht auf
                      derselben Fläche liegen.
                    -->
                    <div class="flex flex-column gap-1">

                        <span class="text-sm font-medium">Datensätze</span>

                        <span class="text-xs text-color-secondary flex align-items-center gap-1"
                              pTooltip="Zeile der Tabelle cp_provision_type_matrix_assignment — sie legt fest, FÜR WELCHES Geschäft diese Zuordnung gilt.">
                            <span>
                                Zuordnung (ID <span class="select-all font-medium">{{ displayRow.assignment.assignmentId }}</span>)
                            </span>
                            @if (displayRow.assignment.assignmentId != null) {
                                <p-button type="button"
                                          icon="fa-regular fa-arrow-up-right-from-square"
                                          size="small"
                                          [text]="true"
                                          [rounded]="true"
                                          pTooltip="Zuordnung öffnen — Strg/Cmd-Klick in einem neuen Fenster"
                                          tooltipPosition="top"
                                          (onClick)="openObject('cp.ProvisionTypeMatrixAssignment', displayRow.assignment.assignmentId, $event)">
                                </p-button>
                            }
                        </span>

                        <!-- Die konfigurierte Reihenfolge. Bewusst hier bei den technischen
                             Angaben und weit weg von der Faktor-Spalte: das ist eine Ordnungszahl
                             der Konfiguration, keine betriebswirtschaftliche Größe. Die Zahl ist
                             zum Nachschlagen da, nicht zum Sortieren — sie ist deshalb auch die
                             einzige Spalte, die keinen Sortierkopf bekommen hat. -->
                        @if (displayRow.assignment.orderPriority != null) {
                            <span class="text-xs text-color-secondary"
                                  pTooltip="Konfigurierte Reihenfolge der Prüfung: Die Berechnung geht die Zuordnungen aufsteigend durch, die niedrigste Zahl zuerst, und nimmt die erste passende.">
                                Priorität <span class="select-all font-medium">{{ displayRow.assignment.orderPriority }}</span>
                            </span>
                        }

                        <!-- Nur wenn es überhaupt eine Matrixzeile gibt: die Zeilen „kein eigener
                             Satz" haben keine, dort stünde sonst „ID undefined". -->
                        @if (displayRow.row?.matrixRowId != null) {
                            <span class="text-xs text-color-secondary flex align-items-center gap-1"
                                  pTooltip="Zeile der Tabelle cp_provision_type_matrix_agent — sie legt fest, WIE VIEL vergütet wird.">
                                <span>
                                    Matrixzeile (ID <span class="select-all font-medium">{{ displayRow.row.matrixRowId }}</span>)
                                </span>
                                <p-button type="button"
                                          icon="fa-regular fa-arrow-up-right-from-square"
                                          size="small"
                                          [text]="true"
                                          [rounded]="true"
                                          pTooltip="Matrixzeile öffnen — Strg/Cmd-Klick in einem neuen Fenster"
                                          tooltipPosition="top"
                                          (onClick)="openObject('cp.ProvisionTypeMatrixAgent', displayRow.row.matrixRowId, $event)">
                                </p-button>
                            </span>
                        }

                    </div>

                    <!--
                      „Zugeordnete Matrix" used to stand here as a section of its own and was dropped
                      WITHOUT replacement: it is always the first step of the inheritance path
                      further down (built that way in the service, measured that way on all 6.300
                      assignments of the stock). What the section carried as a statement of its own
                      now sits in the tooltip of the first chip; its name stays in the filter text,
                      whoever searches for it still finds the row. The case „Matrix nicht aktiv" — in
                      which both are empty — is shown explicitly below instead of staying invisible.
                    -->

                    @if (displayRow.row) {
                        <div class="flex flex-column gap-1">
                            <span class="text-sm font-medium">Gültigkeit der Zeile</span>
                            <span class="text-sm">
                                {{ periodLabel(displayRow.row.startDate, displayRow.row.endDate) }}
                            </span>
                            <!-- The green chip „zum Stichtag gültig" is gone: it stood on all
                                 10.443 measured rows and was therefore no information. The
                                 OPPOSITE case gets a marker of its own instead — it does not occur
                                 in the stock today but is possible in code. -->
                            @if (displayRow.invalidAtKeyDate) {
                                <p-tag value="zum Stichtag nicht gültig"
                                       icon="fa-solid fa-calendar-xmark"
                                       styleClass="cp-config-tag-warn align-self-start">
                                </p-tag>
                                <span class="text-xs text-color-secondary" style="max-width: 22rem">
                                    Diese Zeile gilt für Verträge mit Beginn am
                                    {{ germanDate(configuration?.keyDate) }} nicht — die Berechnung
                                    überspringt sie.
                                </span>
                            }
                        </div>
                    }

                    <!--
                      Origin of the liability type — ALWAYS visible, with four statements.

                      Until now the whole section hung on `liabilitySourceInfo.label` and vanished
                      when no source was set. Yet exactly that state is a statement itself: no
                      liability, no liability period, no charge-back. And the split „Zuweisung"
                      against „generell" is the core of the remark: 5.431 of the 9.032 measured
                      liability statements come from the one unrestricted default row and could not
                      be told from specific assignments.
                    -->
                    <div class="flex flex-column gap-1">
                        <span class="text-sm font-medium">Herkunft der Haftungsart</span>
                        <span class="text-sm font-semibold">{{ displayRow.liabilityOriginTitle }}</span>
                        <span class="text-xs text-color-secondary max-w-22rem">
                            {{ displayRow.liabilityOriginDetail }}
                        </span>
                        @if (displayRow.liabilityTypeMissing) {
                            <span class="text-xs text-color-secondary max-w-22rem">
                                Der Haftungstyp selbst ist nicht aktiv und deshalb nicht benennbar —
                                siehe die Warnung oben auf der Seite.
                            </span>
                        }
                        @if (displayRow.row?.liabilityAmbiguous) {
                            <span class="text-xs text-color-secondary max-w-22rem">
                                Die Haftungszuordnung schränkt ein Merkmal ein, das diese
                                Zuordnung offen lässt — welche Haftungsart am Ende gilt,
                                entscheidet sich erst am konkreten Vertrag.
                            </span>
                        }
                    </div>

                </div>

                <div class="flex flex-column gap-2">

                    <span class="text-sm font-medium">Vererbungspfad</span>

                    <span class="text-xs text-color-secondary">
                        Von der zugeordneten Matrix aufwärts — der erste Schritt IST die Matrix, die
                        dieser Zuordnung zugewiesen ist. Die Zeilen der übergeordneten Matrizen
                        werden angehängt, nicht ersetzt; deshalb zeigt die Tabelle Bänder und keine
                        Einzelwerte.
                    </span>

                    @if (displayRow.pathSteps.length) {

                        <div class="flex flex-wrap align-items-center gap-2">
                            @for (step of displayRow.pathSteps; track $index) {
                                @if (!$first) {
                                    <i class="fa-solid fa-arrow-right text-xs text-color-secondary"></i>
                                }
                                <!-- The step actually used carries a STAR and is green:
                                     green means „das gilt" and stands clearly against the other
                                     levels and the red „Kette endet hier". Which step it is is
                                     decided by the matrix id where the endpoint delivers one,
                                     otherwise by the name (which is unique in the stock).
                                     The colour comes from the class, NOT from `severity` — see
                                     the reasoning in this component's SCSS. -->
                                <p-tag [value]="step.name"
                                       [icon]="$index === displayRow.activePathIndex ? 'fa-solid fa-star' : null"
                                       [styleClass]="($index === displayRow.activePathIndex ? 'cp-config-tag-success ' : '')
                                           + (step.matrixId != null ? 'cursor-pointer' : '')"
                                       [attr.role]="step.matrixId != null ? 'link' : null"
                                       [attr.tabindex]="step.matrixId != null ? 0 : null"
                                       [pTooltip]="($index === displayRow.activePathIndex
                                               ? 'Aus dieser Stufe stammt der angezeigte Wert. ' : '')
                                           + step.tooltip
                                           + (step.matrixId != null
                                               ? ' — anklicken zum Öffnen, Strg/Cmd-Klick in einem neuen Fenster.' : '')"
                                       (click)="openObject('cp.ProvisionTypeMatrix', step.matrixId, $event)"
                                       (keydown.enter)="openObject('cp.ProvisionTypeMatrix', step.matrixId)">
                                </p-tag>
                            }
                            @if (displayRow.assignment.inheritanceChainTruncated) {
                                <i class="fa-solid fa-arrow-right text-xs text-color-secondary"></i>
                                <p-tag value="Kette endet hier"
                                       icon="fa-solid fa-triangle-exclamation"
                                       styleClass="cp-config-tag-danger">
                                </p-tag>
                            }
                        </div>

                    } @else {

                        <!-- No path means: the assigned matrix is inactive, deleted or
                             belongs to another Provisionsart. Before, this case was invisible in
                             the detail — path empty, section gone, warning inside the section
                             gone as well. -->
                        <p-tag value="Matrix nicht aktiv oder nicht in dieser Provisionsart"
                               icon="fa-solid fa-triangle-exclamation"
                               styleClass="cp-config-tag-danger align-self-start">
                        </p-tag>

                    }

                    <!-- STANDS OUTSIDE the path branch: the assignment's warning matters
                         most exactly when there is no path to show. -->
                    @if (displayRow.assignment.warning) {
                        <p-message severity="warn"
                                   [text]="displayRow.assignment.warning"
                                   styleClass="w-full">
                        </p-message>
                    }

                </div>

                <!--
                  Die Kandidaten, die die Berechnung nie erreicht. Sie standen bis hierher als
                  gleichwertige Zeilen in der Tabelle — eine Wildcard-Zeile mit Faktor 0 liest sich
                  dort wie „für dieses Geschäft gibt es nichts", und das ist falsch: sie kommt gar
                  nicht zum Zug. Hier stehen sie vollständig, mit Faktor, Berechnungsart und
                  Herkunftsmatrix, nur eben als das, was sie sind.
                -->
                @if (displayRow.unreachableCandidates?.length) {

                    <div class="flex flex-column gap-2">

                        <span class="text-sm font-medium">
                            Nicht erreichbare Kandidaten ({{ displayRow.unreachableCandidates.length }})
                        </span>

                        <span class="text-xs text-color-secondary" style="max-width: 44rem">
                            Frühere Zeilen dieser Zuordnung decken bereits jede Laufzeit- und
                            Alterskombination ab, die diese Zeile abdecken würde — entweder weil
                            eine von ihnen ohne Einschränkung gilt, also ohne Laufzeitband und ohne
                            Altersband, oder weil ihr Band dieses hier vollständig enthält.
                            Die Berechnung nimmt die erste passende Zeile und kommt hier nie an.
                            Diese Zeilen stehen weiterhin in der Konfiguration; sie sind nur aus
                            der Tabelle heraus, weil sie dort wie gültige Werte aussahen.
                        </span>

                        <div class="flex flex-column gap-2">
                            @for (candidate of displayRow.unreachableCandidates; track $index) {
                                <div class="flex flex-wrap align-items-center gap-2">

                                    <cp-form-field-output-factor
                                            [value]="candidate.factor"
                                            [calculationMode]="candidate.calculationMode"
                                            [calculationModeInfo]="candidate.calculationModeInfo">
                                    </cp-form-field-output-factor>

                                    <cp-form-field-output-origin
                                            [value]="candidate.origin"
                                            [enumInfo]="candidate.originInfo"
                                            [matrixName]="candidate.originMatrixName">
                                    </cp-form-field-output-origin>

                                    <!-- Auch die toten Kandidaten sind Datensätze, die jemand
                                         nachschlagen will — sonst wäre ausgerechnet der Abschnitt
                                         nicht auffindbar, der erklärt, warum eine Zeile fehlt. -->
                                    @if (candidate.matrixRowId != null) {
                                        <span class="text-xs text-color-secondary white-space-nowrap flex align-items-center gap-1"
                                              pTooltip="Zeile der Tabelle cp_provision_type_matrix_agent">
                                            <span>ID <span class="select-all">{{ candidate.matrixRowId }}</span></span>
                                            <p-button type="button"
                                                      icon="fa-regular fa-arrow-up-right-from-square"
                                                      size="small"
                                                      [text]="true"
                                                      [rounded]="true"
                                                      pTooltip="Matrixzeile öffnen — Strg/Cmd-Klick in einem neuen Fenster"
                                                      tooltipPosition="top"
                                                      (onClick)="openObject('cp.ProvisionTypeMatrixAgent', candidate.matrixRowId, $event)">
                                            </p-button>
                                        </span>
                                    }

                                </div>
                            }
                        </div>

                    </div>

                }

                <!-- Gruppenvergleich in ausführlicher Form. In der Tabelle stehen die fremden
                     Werte als zusätzliche Zeilen unter den eigenen; hier stehen sie je Gruppe
                     beisammen. -->
                @if (includeGroupComparison && displayRow.assignment.groupComparison?.length) {

                    <div class="flex flex-column gap-2">

                        <span class="text-sm font-medium">Andere Vermittlergruppen</span>

                        @for (comparison of displayRow.assignment.groupComparison;
                              track comparison.provisionTypeGroupId) {
                            <div class="flex flex-wrap align-items-center gap-3">
                                <span class="text-sm font-medium w-12rem">
                                    {{ comparison.provisionTypeGroupName }}
                                </span>
                                @for (comparisonRow of comparison.rows; track comparisonRow.matrixRowId) {
                                    <cp-form-field-output-factor
                                            [value]="comparisonRow.factor"
                                            [calculationMode]="comparisonRow.calculationMode"
                                            [calculationModeInfo]="comparisonRow.calculationModeInfo">
                                    </cp-form-field-output-factor>
                                }
                                @if (!comparison.rows?.length) {
                                    <span class="text-sm text-color-secondary">keine Zeile</span>
                                }
                            </div>
                        }

                    </div>

                }

            </div>

        </td>
    </tr>

</ng-template>
